import { z } from 'zod';
import type { IntlFormatters } from 'react-intl';

type FormatMessage = IntlFormatters['formatMessage'];

// Same rule as addressRegex in trafikportalen-api logic/models.go. The API spec
// has no pattern for it, so it cannot be taken from the generated schemas.
const STREET_PATTERN = /^[\p{L}\d .,-]+$/u;
const STREET_CHARACTER = /[\p{L}\d .,-]/u;
const INVISIBLE_CHARACTER = /[\s\p{C}]/u;
const CARE_OF = /c\/o/i;

export const invalidStreetCharacters = (street: string): string[] => [
  ...new Set([...street].filter((char) => !STREET_CHARACTER.test(char))),
];

interface StreetOptions {
  hasCareOfField?: boolean;
}

export const streetFieldError = (
  street: string,
  formatMessage: FormatMessage,
  { hasCareOfField = false }: StreetOptions = {},
): true | string => {
  if (!street || STREET_PATTERN.test(street)) return true;

  if (hasCareOfField && CARE_OF.test(street)) {
    return formatMessage({
      defaultMessage: 'Ange c/o i fältet c/o, inte i adressen.',
    });
  }

  const characters = [
    ...new Set(
      invalidStreetCharacters(street).map((char) =>
        INVISIBLE_CHARACTER.test(char)
          ? formatMessage({ defaultMessage: 'osynligt tecken' })
          : char,
      ),
    ),
  ];

  return formatMessage(
    {
      defaultMessage:
        '{count, plural, one {Otillåtet tecken} other {Otillåtna tecken}}: {characters}. Adressen får bara innehålla bokstäver, siffror, mellanslag, punkt, komma och bindestreck.',
    },
    { count: characters.length, characters: characters.join(', ') },
  );
};

export const streetSchema = (
  formatMessage: FormatMessage,
  options?: StreetOptions,
) =>
  z.string().superRefine((street, ctx) => {
    const error = streetFieldError(street, formatMessage, options);
    if (error !== true) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
    }
  });
